<script setup lang="ts">
import { computed, ref, ComponentPublicInstance, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { GlobalMethods, x509Certificate } from 'e.firma'
import { CNivelCarrera, ECargo, NOMBRE_CARGO } from '@shared/types/enums'
import FileInput from '@/components/FileInput.vue'
import { formatDate } from '@/utils/dateFormatter'
import { IResponsable } from '@shared/types/base'
interface ArchivosFirma {
	certificate?: File
}
const props = defineProps<{
	isMenuOpen: boolean
	responsables: { [key: string]: IResponsable }
}>()
const errorMessage = ref<string>('')
const showErrorModal = ref(false)
const emit = defineEmits(['update:isMenuOpen', 'update:responsables'])
const responsablesLocal = ref<{ [key: string]: IResponsable }>({ ...props.responsables })

// Sincronizar el ref local cuando cambie el prop desde el componente padre
watch(
	() => props.responsables,
	newValue => {
		if (newValue) {
			responsablesLocal.value = { ...newValue }
		}
	},
	{ deep: true }
)

const fileInputs = {
	certificate: {
		label: 'Certificado (.cer)',
		accept: '.cer',
		icon: 'fa-certificate'
	}
}

const fileBase64 = ref<string>('')
const isUploading = ref(false)
const archivos = ref<ArchivosFirma>({})
const nombre = ref<string>('')
const primerApellido = ref<string>('')
const segundoApellido = ref<string>('')
const phone = ref<string>('')
const refs = {
	certificate: ref<HTMLInputElement | null>(null)
}
const selectedFile = ref<File | null>(null)
const uploadError = ref<string>('')
const certificado = ref<x509Certificate | null>(null)
const certificateCargo = ref<number | null>(null)
const degreeCargo = ref<number | null>(null)
const degreeAbrTitulo = ref<string | null>(null)
const certificateCurp = ref<string>('')
const optionsCargo = computed(() => {
	const valores: { value: number; text: string }[] = []
	// Iterar sobre las entradas del enum para obtener solo los valores numéricos
	Object.entries(ECargo).forEach(([, value]) => {
		// Solo incluir valores numéricos (no las claves string)
		if (typeof value === 'number') {
			valores.push({ value, text: NOMBRE_CARGO[value as ECargo] })
		}
	})
	return valores
})

const optionsCargoCertificados = computed(() => {
	const valores: { value: number; text: string }[] = []
	// Iterar sobre las entradas del enum para obtener solo los valores numéricos
	Object.entries(ECargo).forEach(([, value]) => {
		// Solo incluir valores numéricos (no las claves string)
		if (typeof value === 'number' && value <= 5) {
			valores.push({ value, text: NOMBRE_CARGO[value as ECargo] })
		}
	})
	return valores
})
const opcionesNivelEstudios = computed(() =>
	CNivelCarrera.map(item => ({
		value: item.value,
		text: item.text
	}))
)
const cancelUpload = () => {
	selectedFile.value = null
	fileBase64.value = ''
	uploadError.value = ''
	certificado.value = null
	certificateCurp.value = ''
	certificateCargo.value = null
	degreeCargo.value = null
	degreeAbrTitulo.value = null
	nombre.value = ''
	primerApellido.value = ''
	segundoApellido.value = ''
	phone.value = ''
	emit('update:isMenuOpen', false)
}
const setFileRef = (el: Element | ComponentPublicInstance | null, tipo: keyof typeof refs) => {
	if (el instanceof HTMLInputElement || el === null) {
		refs[tipo].value = el
	}
}

const onFileSelected = async (event: Event): Promise<void> => {
	const input = event.target as HTMLInputElement
	if (input.files?.[0]) {
		selectedFile.value = input.files[0]
	}

	// Validar que el archivo sea un certificado .cer
	if (!selectedFile.value?.name.toLowerCase().endsWith('.cer')) {
		uploadError.value = 'Por favor selecciona un archivo .cer válido'
		selectedFile.value = null
		return
	}

	try {
		// Leer el archivo como base64
		const fileBase64Local = await getFile(selectedFile.value)

		// Convertir de base64 a binario usando GlobalMethods
		const certificadoBinary = GlobalMethods.base64ToBinary(fileBase64Local)

		// Crear instancia del certificado X.509 para validar que sea válido
		certificado.value = new x509Certificate(certificadoBinary)

		if (certificado.value.certificate.validity?.notAfter < new Date()) {
			uploadError.value = 'El certificado ha expirado'
			selectedFile.value = null
			return
		}
		// Si llegamos aquí, el certificado es válido
		certificateCurp.value = certificado.value.certificate.subject.attributes[6].value
		// Limpiar cualquier error previo
		uploadError.value = ''
		fileBase64.value = fileBase64Local
	} catch (error) {
		console.error('Error al leer el certificado:', error)
		uploadError.value = 'Error al leer el archivo del certificado. Asegúrate de que sea un archivo .cer válido.'
		selectedFile.value = null
	}
}
const getFile = async (file: File): Promise<string> =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader()

		reader.onload = () => {
			if (!reader.result) {
				reject(new Error('No se pudo leer el archivo'))
				return
			}

			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1]
				resolve(base64)
			} else {
				const uint8Array = new Uint8Array(reader.result)
				const textDecoder = new TextDecoder('utf-8')
				resolve(textDecoder.decode(uint8Array))
			}
		}

		reader.onerror = () => {
			reject(new Error('Error al leer el archivo: ' + (reader.error?.message || 'Error desconocido')))
		}

		reader.readAsDataURL(file)
	})

const clearCertificate = () => {
	certificateCurp.value = ''
	certificateCargo.value = null
	degreeCargo.value = null
	degreeAbrTitulo.value = null
	nombre.value = ''
	primerApellido.value = ''
	segundoApellido.value = ''
	phone.value = ''
	fileBase64.value = ''
	certificado.value = null
	selectedFile.value = null
	uploadError.value = ''
}
const saveResponsable = () => {
	try {
		const curp = certificateCurp.value?.trim()
		if (!curp) throw new Error('No se pudo obtener la CURP del certificado.')
		if (!fileBase64.value) throw new Error('No se pudo leer el certificado en base64.')

		const yaExiste = Object.prototype.hasOwnProperty.call(responsablesLocal.value, curp)
		const yaExisteResponsableCertificados = Object.values(responsablesLocal.value).some(responsable => responsable.certificates)
		let yaExisteCargoCertificados = false
		let yaExisteCargoTitulo = false

		if (Object.keys(responsablesLocal.value).length > 0) {
			yaExisteCargoCertificados = Object.keys(responsablesLocal.value).some(key => responsablesLocal.value[key]?.certificates?.idCargo === (certificateCargo.value ?? 0))
			yaExisteCargoTitulo = Object.keys(responsablesLocal.value).some(key => responsablesLocal.value[key]?.degrees?.idCargo === (degreeCargo.value ?? 0))
		}
		if (yaExiste) {
			errorMessage.value = 'Ya existe un responsable registrado con esa CURP.'
			showErrorModal.value = true
			cancelUpload()
			return
		}
		if (yaExisteCargoCertificados) {
			errorMessage.value = 'Ya existe un responsable registrado con ese cargo de certificados.'
			showErrorModal.value = true
			cancelUpload()
			return
		}
		if (yaExisteCargoTitulo) {
			errorMessage.value = 'Ya existe un responsable registrado con ese cargo de títulos.'
			showErrorModal.value = true
			cancelUpload()
			return
		}
		if (yaExisteResponsableCertificados && certificateCargo.value !== null && certificateCargo.value !== undefined && certificateCargo.value !== 0) {
			errorMessage.value = 'Ya existe un responsable registrado con certificados. Solo se permite un responsable con certificados.'
			showErrorModal.value = true
			cancelUpload()
			return
		}
		responsablesLocal.value[curp] = {
			cer: {
				certificadoResponsable: fileBase64.value
			},
			certificates: {
				cargo: NOMBRE_CARGO[certificateCargo.value as unknown as ECargo],
				idCargo: certificateCargo.value as unknown as number
			},
			degrees: {
				cargo: NOMBRE_CARGO[degreeCargo.value as unknown as ECargo],
				idCargo: degreeCargo.value as unknown as number,
				abrTitulo: degreeAbrTitulo.value
			},
			nombre: nombre.value,
			primerApellido: primerApellido.value,
			segundoApellido: segundoApellido.value,
			phone: phone.value
		}

		if (certificateCargo.value === null || certificateCargo.value === undefined || certificateCargo.value === 0) {
			delete responsablesLocal.value[curp].certificates
		}
		if (degreeCargo.value === null || degreeCargo.value === undefined || degreeCargo.value === 0) {
			delete responsablesLocal.value[curp].degrees
		}
		clearCertificate()
		emit('update:responsables', responsablesLocal.value)
		emit('update:isMenuOpen', false)
	} catch (err: any) {
		console.error(err)
		cancelUpload()
	}
}
</script>
<template>
	<BaseModal v-model="props.isMenuOpen" blur title="Subir Certificado Digital (.cer)" hideDefaultActions message="Subir Certificado Digital (.cer)" :testMode="false" size="xxxxxlarge">
		<template #body>
			<div class="upload-container">
				<!-- File Input -->
				<div class="file-input-section">
					<FileInput
						v-for="(config, tipo) in fileInputs"
						:key="tipo"
						:id="tipo"
						:label="config.label"
						:accept="config.accept"
						:icon="config.icon"
						:value="archivos[tipo] || null"
						@change="(event: Event) => onFileSelected(event)"
						:ref="(el: Element | ComponentPublicInstance | null) => setFileRef(el, tipo)" />
					<div v-if="uploadError" class="va-input__error">{{ uploadError }}</div>
					<div v-if="selectedFile" class="file-info">
						<VaIcon name="description" class="file-icon" />
						<span>{{ selectedFile.name }} ({{ (selectedFile.size / 1024).toFixed(2) }} KB)</span>
					</div>

					<VaCard class="certificate-card" flat style="margin-top: 10px">
						<VaCardTitle class="card-title">
							<VaIcon name="verified" class="title-icon" />
							Información del responsable de firma
						</VaCardTitle>
						<VaCardContent>
							<div class="certificate-info">
								<div class="certificate-item">
									<VaIcon name="fingerprint" class="certificate-icon" />
									<div class="certificate-content">
										<label class="certificate-label" for="certificate-serial">Número de serie</label>
										<span class="certificate-value">{{ certificado?.serialNumber }}</span>
									</div>
								</div>
								<div class="certificate-item">
									<VaIcon name="event" class="certificate-icon" />
									<div class="certificate-content">
										<label class="certificate-label" for="certificate-notBefore">Fecha de emisión</label>
										<span class="certificate-value">{{ formatDate(certificado?.certificate.validity?.notBefore) }}</span>
									</div>
								</div>
								<div class="certificate-item">
									<VaIcon name="schedule" class="certificate-icon" />
									<div class="certificate-content">
										<label class="certificate-label" for="certificate-notAfter">Fecha de expiración</label>
										<span class="certificate-value">{{ formatDate(certificado?.certificate.validity?.notAfter) }}</span>
									</div>
								</div>
							</div>
							<div class="form-grid">
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Nombre del responsable</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<va-input v-model="nombre" placeholder="Ingrese el nombre del responsable" class="form-input" :rules="[value => !!value || 'El nombre del responsable es requerido']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Primer apellido del responsable</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<va-input
													v-model="primerApellido"
													placeholder="Ingrese el primer apellido del responsable"
													class="form-input"
													:rules="[value => !!value || 'El primer apellido del responsable es requerido']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Segundo apellido del responsable</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<va-input
													v-model="segundoApellido"
													placeholder="Ingrese el segundo apellido del responsable"
													class="form-input"
													:rules="[value => !!value || 'El segundo apellido del responsable es requerido']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Teléfono del responsable</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<va-input
													v-model="phone"
													type="tel"
													placeholder="Ingrese el teléfono del responsable"
													class="form-input"
													:rules="[
														value => !!value || 'El teléfono del responsable es requerido',
														value => value.length === 10 || 'El número de teléfono debe tener 10 dígitos',
														value => /^\d*$/.test(value) || 'El campo solo permite números'
													]" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Cargo del responsable para certificados</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<div class="cargo-select">
													<select v-model="certificateCargo" class="form-select form-input">
														<option :value="null" disabled>Seleccione un cargo...</option>
														<option v-for="opt in optionsCargoCertificados" :key="opt.value" :value="opt.value">
															{{ opt.text }}
														</option>
													</select>
													<button type="button" class="btn btn-outline-secondary btn-sm" :disabled="certificateCargo === null" @click="certificateCargo = null">Limpiar</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Cargo del responsable para títulos</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<div class="cargo-select">
													<select v-model="degreeCargo" class="form-select form-input">
														<option :value="null" disabled>Seleccione un cargo...</option>
														<option v-for="opt in optionsCargo" :key="opt.value" :value="opt.value">
															{{ opt.text }}
														</option>
													</select>
													<button type="button" class="btn btn-outline-secondary btn-sm" :disabled="degreeCargo === null" @click="degreeCargo = null">Limpiar</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" style="margin-top: 10px">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Nivel de estudios</h3>
												</div>
											</div>
											<div class="card-value transaction-value">
												<select v-model="degreeAbrTitulo" class="form-select form-input">
													<option :value="null" disabled>Seleccione un nivel de estudios...</option>
													<option v-for="opt in opcionesNivelEstudios" :key="opt.value" :value="opt.value">
														{{ opt.text }}
													</option>
												</select>
											</div>
										</div>
									</div>
								</div>
							</div>
						</VaCardContent>
					</VaCard>
				</div>

				<!-- Estado de conversión -->
				<div v-if="isUploading" class="conversion-status">
					<VaProgressCircle indeterminate size="large" />
					<p>Convirtiendo archivo a base64...</p>
				</div>
			</div>
		</template>
		<!-- Botones de acción -->
		<template #footer>
			<div class="modal-actions">
				<VaButton color="secondary" @click="cancelUpload" :disabled="isUploading"> Cancelar </VaButton>
				<VaButton color="primary" :disabled="!selectedFile || isUploading" :loading="isUploading" @click="saveResponsable">
					<VaIcon name="check" class="me-2" />
					Finalizar
				</VaButton>
			</div>
		</template>
	</BaseModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;

	border-left: 4px solid #f71d1d;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.cargo-select {
	display: flex;
	gap: 0.5rem;
	align-items: center;
}

.cargo-select .form-select {
	flex: 1;
}

.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

.package-card:hover {
	transform: translateY(-8px) scale(1.02);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.package-card:hover::before {
	opacity: 1;
}

/* Gradientes de tarjetas */
.card-gradient {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 120px;
	border-radius: 16px 16px 0 0;
	opacity: 0.15;
	transition: opacity 0.3s ease;
}

.transaction-gradient {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
}
.package-card:hover .card-gradient {
	opacity: 0.25;
}

.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}

.card-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.card-title-group {
	flex: 1;
}

.card-title {
	font-size: 1.125rem;
	font-weight: 700;
	color: #1a202c;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.3px;
}

/* Avatares */
.package-avatar {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;
}

.package-avatar::before {
	content: '';
	position: absolute;
	top: -50%;
	left: -50%;
	width: 200%;
	height: 200%;
	background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	transform: rotate(45deg);
	transition: all 0.5s ease;
}

.package-card:hover .package-avatar::before {
	animation: shine 0.6s ease;
}

.transaction-avatar {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
}

.card-value {
	font-size: 16px;
	font-weight: 500;
	margin-top: 4rem;
	transition: all 0.3s ease;
}

.title-icon {
	color: #667eea;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

.upload-container {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.file-input-section {
	margin-bottom: 15px;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
	background: #f8f9fa;
	border-radius: 12px;
	margin-top: 10px;
}

.file-icon {
	color: #667eea;
	font-size: 20px;
}

.conversion-status {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 15px;
	padding: 30px;
	background: #f8f9fa;
	border-radius: 16px;
	border: 2px dashed #667eea;
}

.conversion-status p {
	margin: 0;
	color: #6c757d;
	font-weight: 600;
	font-size: 16px;
}

.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 15px 0;
}

.va-input__error {
	color: #dc3545;
	font-size: 14px;
	margin-top: 5px;
	font-weight: 600;
}

/* Estilos compartidos para el certificado dentro del modal */
.certificate-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	transition:
		transform 0.3s ease,
		box-shadow 0.3s ease;
}

.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding: 15px 0;
}

.certificate-item {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 15px;
	background: #f8f9fa;
	border-radius: 16px;
	border-left: 5px solid #28a745;
	transition: all 0.3s ease;
}

.certificate-item:hover {
	transform: translateX(5px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.certificate-icon {
	color: #4aec3c;
	font-size: 15px;
}

.certificate-content {
	flex: 1;
}

.certificate-label {
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 5px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.certificate-value {
	display: block;
	font-size: 16px;
	font-weight: 700;
	color: #1a2a3a;
	word-break: break-all;
}

/* Responsive design para el modal */
@media (max-width: 768px) {
	.modal-actions {
		flex-direction: column;
	}

	.file-info {
		flex-direction: column;
		text-align: center;
	}

	.conversion-status {
		padding: 20px;
	}

	.card-title {
		padding: 15px 15px 0 15px;
		font-size: 12px;
	}

	.certificate-item {
		padding: 10px;
		gap: 10px;
	}

	.certificate-value {
		font-size: 14px;
	}
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 1.5rem;
	margin-bottom: 2rem;
}

.label-icon {
	color: #fcfcfc;
	font-size: 1.125rem;
}

.form-input {
	width: 100%;
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Responsive del formulario */
@media (max-width: 768px) {
	.form-grid {
		grid-template-columns: 1fr;
		gap: 1.25rem;
	}
}
</style>
